import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Send } from 'lucide-react';
import * as Icons from 'lucide-react';
import { innovativeSolutions, globalRequests } from '../data/mock';
import { RequestDialog } from '../components/RequestDialog';

export const Solutions = () => {
  const [isRequestOpen, setIsRequestOpen] = React.useState(false);
  const [activeConfig, setActiveConfig] = React.useState({});

  const handleConsultation = (source = "General") => {
    setActiveConfig(globalRequests.consultation);
    setIsRequestOpen(true);
  };

  const getIcon = (iconName) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon size={40} className="text-gold" /> : null;
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      <RequestDialog
        isOpen={isRequestOpen}
        onOpenChange={setIsRequestOpen}
        config={activeConfig}
        context="Solutions Page"
      />
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-6">
            <span className="text-gold text-sm font-medium uppercase tracking-wide">Modern Legal Services</span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-serif font-bold text-white mb-6">
            Innovative <span className="text-gold">Solutions</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Beyond traditional legal services — flexible, transparent solutions that are designed for the modern business.
          </p>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Structure your legal support the way you need it.
          </p>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {innovativeSolutions.map((solution, index) => (
              <div
                key={solution.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gold/10 border border-gold/30 rounded-full mb-6">
                    {getIcon(solution.icon)}
                    <span className="text-gold text-sm font-semibold uppercase tracking-wide ml-2">
                      {solution.prefix}
                    </span>
                  </div>

                  <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
                    {solution.title}
                  </h2>

                  <p className="text-gold text-xl font-semibold mb-6 italic">
                    {solution.tagline}
                  </p>

                  <p className="text-gray-300 text-lg leading-relaxed mb-8">
                    {solution.description}
                  </p>

                  <div className="space-y-3 mb-8">
                    <h4 className="text-gold font-semibold text-sm uppercase tracking-wider mb-4">
                      What's Included
                    </h4>
                    <ul className="space-y-3">
                      {solution.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-gray-300">
                          <CheckCircle size={20} className="text-gold mr-3 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl">
                    <p className="text-sm text-gray-400 mb-2">
                      <span className="text-gold font-semibold">Ideal For:</span>
                    </p>
                    <p className="text-gray-300">{solution.idealFor}</p>
                  </div>
                </div>

                {/* Visual Card */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 to-gold/5 blur-2xl opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>

                    <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 border border-gold/30 rounded-2xl p-12 text-center hover:border-gold/50 transition-all duration-300">
                      <div className="w-24 h-24 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-8">
                        {getIcon(solution.icon)}
                      </div>

                      <h3 className="text-3xl font-bold text-white mb-4">
                        {solution.title}
                      </h3>

                      <p className="text-gold text-lg font-semibold mb-6 italic">
                        {solution.tagline}
                      </p>

                      <div className="flex items-center justify-center space-x-2">
                        <div className="h-1 w-12 bg-gold rounded-full"></div>
                        <div className="h-1 w-12 bg-gold/50 rounded-full"></div>
                        <div className="h-1 w-12 bg-gold/25 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              Why Our <span className="text-gold">Solutions</span> Work
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Traditional legal services meet modern business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl">
              <div className="text-4xl font-bold text-gold mb-3">Flexible</div>
              <h3 className="text-xl font-semibold text-white mb-3">Engagement Models</h3>
              <p className="text-gray-400">
                Pay for what you need, when you need it. No long-term lock-ins or surprise billings.
              </p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl">
              <div className="text-4xl font-bold text-gold mb-3">Transparent</div>
              <h3 className="text-xl font-semibold text-white mb-3">Pricing</h3>
              <p className="text-gray-400">
                Fixed fees and clear scopes. Know exactly what you're paying for upfront.
              </p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl">
              <div className="text-4xl font-bold text-gold mb-3">Responsive</div>
              <h3 className="text-xl font-semibold text-white mb-3">Support</h3>
              <p className="text-gray-400">
                Fast turnarounds and direct access to senior counsel. No layers of bureaucracy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
            Find the Right <span className="text-gold">Solution</span>
          </h2>

          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Not sure which solution fits your needs? Let's have a conversation to understand your
            challenges and craft a bespoke solution around your goals.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={handleConsultation}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-gold to-gold-light text-black font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-gold/40 transform hover:scale-105 transition-all duration-300"
            >
              Let's Talk
              <Send size={20} className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
            </button>
            <Link
              to="/practice-areas"
              className="inline-flex items-center px-8 py-4 border-2 border-gold text-gold font-semibold text-lg rounded-lg hover:bg-gold hover:text-black transition-all duration-300"
            >
              View Practice Areas
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
